import {Modal} from '@/components/ui/modal/Modal'
import {
    StyledBody,
    StyledButton,
    StyledContent,
    StyledGoalCardWrapper,
    StyledSubtitle,
    StyledTextArea,
    StyledTextAreaWrapper,
    modalStyles
} from './style'
import {Trans, useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button/Button'
import {HostGoalCard} from '../host-goal-card/HostGoalCard'
import {useState} from 'react'
import {HaveBannedElementObj} from '@/features/generated-content-security/types'
import {selectGoalTeaserModal} from '@/store/selectors'
import {useRootStore} from '@/store'
import {useMe} from '@/features/authentication/queries/useMe'
import {HostTypeV3} from '@/features/authentication/types'
import {useParams} from 'react-router-dom'
import {ChannelID, GroupParams} from '@/features/chat/types'
import {getActiveGoalByUserRole} from '@/features/chat/helpers/helpers'
import {useGetChannelDetailsV3} from '@/features/chat/queries/useGetChannelDetailsV3'
import {useSendMessageV3} from '@/features/chat/queries/useSendMessageV3'
import {MessageTypeE} from '@/features/chat/chat.model'
import {HostTooltipSourceE} from '@/features/chat/components/ChatFileInput/tooltip-content/HostTooltipContent'
import {useSendBroadcastMessageV3} from '@/features/chat/queries/useSendBroadcastMessageV3'
import {useSendGroupMessageV3} from '@/features/chat/queries/useSendGroupMessageV3'
import {useCreateBroadcastV3} from '@/features/chat/queries/useCreateBroadcastV3'
import {useGetGoalsForCreator} from '../../queries/useGetGoalsForCreator'

export const GoalTeaserModal = ({
    onSendMessage = null,
    onCloseLocalModal = null,
    isBlockedSending = false,
    subtitleName,
    source = HostTooltipSourceE.CHAT,
    sourceData
}: {
    onSendMessage?: ((params: GroupParams) => void) | null
    onCloseLocalModal?: (() => void) | null
    isBlockedSending?: boolean
    subtitleName?: string
    source?: HostTooltipSourceE
    sourceData?: {
        usersIds: number[]
        goalsIds: number[]
    }
}) => {
    const {t} = useTranslation()
    const {closeModal: onCloseSharedModal, guestID} = useRootStore(selectGoalTeaserModal)
    const {data: userData} = useMe()
    const host = userData as HostTypeV3
    const {data: goalList} = useGetGoalsForCreator()
    const goal = getActiveGoalByUserRole(goalList) // Check if user has an active goal
    const {channel} = useParams()
    const isCreatedBroadcast = channel?.startsWith('group-')
    const groupId = isCreatedBroadcast ? channel?.replace('group-', '') : null
    const {data: channelDetails} = useGetChannelDetailsV3(channel as ChannelID)
    const {mutate: sendChatMessage, isPending: isPendingSendChatMessage} = useSendMessageV3({
        chatIdentifier: String(channel)
    })
    const {mutate: sendBroadcastMessage, isPending: isPendingSendBroadcastMessage} = useSendBroadcastMessageV3()
    const {mutate: createBroadcast, isPending: isPendingCreateBroadcastMessage} = useCreateBroadcastV3({})
    const {mutate: sendGroupMessage, isPending: isPendingSendGroupMessage} = useSendGroupMessageV3()

    const [isActive, setIsActive] = useState(false)
    const [messageText, setMessageText] = useState<string>('')
    const [haveBannedContent, setHaveBannedContent] = useState<HaveBannedElementObj | null>(null)

    const sendTeaserHandler = {
        [HostTooltipSourceE.CHAT]: async () =>
            await sendChatMessage({
                message: {
                    text: messageText,
                    type: MessageTypeE.GOAL_TEASER,
                    senderId: host.id,
                    receiverId: Number(guestID),
                    metadata: {
                        goalId: goal?.id
                    }
                }
            }),
        [HostTooltipSourceE.BROADCAST]: async () =>
            (await isCreatedBroadcast)
                ? sendBroadcastMessage({
                      broadcastGroupId: groupId || '',
                      payload: {
                          teaserGoalId: goal?.id,
                          message: {text: messageText, type: MessageTypeE.GOAL_TEASER, metadata: {goalId: goal?.id}},
                          messageType: MessageTypeE.TEASER
                      }
                  })
                : createBroadcast({
                      teaserGoalId: goal?.id,
                      message: {text: messageText, type: MessageTypeE.GOAL_TEASER, metadata: {goalId: goal?.id}},
                      messageType: MessageTypeE.TEASER,
                      ...sourceData!
                  }),
        [HostTooltipSourceE.GROUPS]: async () =>
            await sendGroupMessage({
                groupId: groupId || '',
                payload: {
                    teaserGoalId: goal?.id,
                    message: {text: messageText, type: MessageTypeE.GOAL_TEASER, metadata: {goalId: goal?.id}},
                    messageType: MessageTypeE.TEASER
                }
            })
    }[source]

    const onChange = (value: string) => {
        setMessageText(value)
    }

    const closeModal = () => {
        onCloseLocalModal ? onCloseLocalModal() : onCloseSharedModal()
    }

    const sendGoalTeaser = () => {
        if (!!onSendMessage && !!onCloseLocalModal && source === HostTooltipSourceE.CHAT) {
            const params = {
                message: messageText,
                teaser: MessageTypeE.GOAL_TEASER as MessageTypeE | undefined,
                goal_id: goal?.id
            }
            onSendMessage(params as GroupParams)
        } else {
            sendTeaserHandler()
        }
        closeModal()
    }

    return (
        <Modal onClose={closeModal} title={t('goal_teaser_modal:title')} modalStyles={modalStyles}>
            <StyledBody>
                <StyledSubtitle>
                    <Trans
                        t={t}
                        i18nKey="goal_teaser_modal:subtitle"
                        values={{LEADER_NAME: subtitleName ?? channelDetails?.user?.username}}
                        components={{strong: <strong />}}
                    />
                </StyledSubtitle>
                <StyledContent isActive={isActive}>
                    <StyledGoalCardWrapper onClick={() => setIsActive(!isActive)}>
                        {!!goal && <HostGoalCard goal={goal} isGoalTeaser={true} isActive={isActive} />}
                    </StyledGoalCardWrapper>
                    {isActive && (
                        <StyledTextAreaWrapper>
                            <StyledTextArea
                                placeholder={t('goal_teaser_modal:placeholder')}
                                onChange={onChange}
                                getHaveBannedElements={setHaveBannedContent}
                                maxChars={120}
                                inModal
                            />
                            <StyledButton>
                                <Button
                                    disabled={
                                        !!haveBannedContent ||
                                        (isBlockedSending && !messageText) ||
                                        isPendingSendChatMessage ||
                                        isPendingSendBroadcastMessage ||
                                        isPendingSendGroupMessage ||
                                        isPendingCreateBroadcastMessage
                                    }
                                    onClick={sendGoalTeaser}
                                >
                                    {t('goal_teaser_modal:button')}
                                </Button>
                            </StyledButton>
                        </StyledTextAreaWrapper>
                    )}
                </StyledContent>
            </StyledBody>
        </Modal>
    )
}
